/**
 * Locale-aware `syntax`/`description` for the autocomplete detail line and the signature-help
 * tooltip. `FUNCTIONS` in `language.ts` stays the single source of truth for WHICH functions exist
 * and their arity — this only resolves the presentational text, from the app's own i18n message
 * catalog (`help.formulas.functions.<NAME>.syntax` / `.description` in `src/i18n/{en,es,pt}.json`,
 * shared with the Help page),
 * keyed by the current UI locale rather than the Spanish text hardcoded in `FUNCTIONS`.
 *
 * `language.ts` itself stays pure (no React/i18n import) — this lives in the Formula UI layer
 * instead, and is built from `Object.keys(FUNCTIONS)` so a function added there is picked up here
 * without a second, hand-maintained name list (a missing translation degrades to `t`'s own
 * dev-only warning + raw-key fallback, never a crash).
 */

import type { MessageKey, Translate } from '@/shared/i18n'
import { FUNCTIONS, type FunctionMetadata } from '@/shared/formula/language'

export function localizedFunctionMetadata(t: Translate): readonly FunctionMetadata[] {
  return Object.keys(FUNCTIONS).map((name) => ({
    name,
    syntax: t(`help.formulas.functions.${name}.syntax` as MessageKey),
    description: t(`help.formulas.functions.${name}.description` as MessageKey),
  }))
}
